/**
 * FolderPage.js  -  /tasks/files/:folderId   (or /tasks/files/shared)
 *
 * One folder: its subfolders, and its files grouped into Documents, Images,
 * Music & audio, Videos and Other. The groups are derived from each file's
 * type - they are not folders on disk - so a file can never be filed in the
 * wrong one.
 *
 * What the user may do with each item comes from the server (can_delete,
 * can_share, can_upload). The page only draws the buttons it was told to;
 * the server enforces the same rules again on every action.
 *
 * Every sub-component is declared at MODULE scope. Declared inside FolderPage
 * they would be a new component type on every render, and the dialogs'
 * inputs would lose focus after each keystroke.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Chip,
  Button,
  IconButton,
  Typography,
  Tooltip,
  TextField,
  Alert,
  Skeleton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Link,
  LinearProgress,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { tokens } from "../../../theme";
import { buildUi, fmtDate } from "../taskShared";
import {
  filesCall,
  fileLink,
  downloadFile,
  KIND_GROUPS,
  fileIcon,
  kindColor,
  fmtBytes,
  isoish,
} from "./filesApi";

import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

/* ================================================================== */

const SubfolderCard = ({ ui, folder, onOpen, onDelete }) => (
  <Box
    sx={{
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderRadius: "12px",
      p: 1.5,
      display: "flex",
      alignItems: "center",
      gap: 1.2,
      cursor: "pointer",
      "&:hover": { borderColor: alpha(ui.accent, 0.6) },
    }}
    onClick={onOpen}
  >
    <FolderOutlinedIcon sx={{ color: ui.accent, fontSize: 30 }} />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" fontWeight={700} color={ui.textPrimary} noWrap>
        {folder.name}
      </Typography>
      <Typography variant="caption" color={ui.textSecondary}>
        {folder.file_count} file{folder.file_count === 1 ? "" : "s"}
        {folder.subfolder_count ? ` · ${folder.subfolder_count} folder(s)` : ""}
      </Typography>
    </Box>
    {folder.can_delete && (
      <Tooltip title="Delete folder">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <DeleteOutlineOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const FileRow = ({ ui, file, showOrigin, onPreview, onDownload, onShare, onDelete }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 1.2,
      px: 1,
      borderBottom: `1px solid ${alpha(ui.border, 0.7)}`,
      flexWrap: { xs: "wrap", sm: "nowrap" },
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        color: kindColor(file.kind),
        bgcolor: alpha(kindColor(file.kind), 0.12),
      }}
    >
      {fileIcon(file, { fontSize: 21 })}
    </Box>

    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="body2"
        fontWeight={700}
        color={ui.textPrimary}
        noWrap
        title={file.original_name}
      >
        {file.original_name}
      </Typography>
      <Typography variant="caption" color={ui.textSecondary} noWrap component="div">
        {fmtBytes(file.size_bytes)} · {file.uploader || "Unknown"} ·{" "}
        {fmtDate(isoish(file.createDate))}
        {showOrigin && file.folder_name ? ` · from ${file.folder_name}` : ""}
      </Typography>
      {file.shares?.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
          {file.shares.map((k) => (
            <Chip
              key={k}
              size="small"
              icon={<GroupsOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              label={k}
              sx={{ height: 20, fontSize: 11 }}
            />
          ))}
        </Stack>
      )}
    </Box>

    <Stack direction="row" spacing={0.3} sx={{ flexShrink: 0 }}>
      {file.previewable && (
        <Tooltip title="Preview">
          <IconButton size="small" onClick={onPreview}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Download">
        <IconButton size="small" onClick={onDownload}>
          <DownloadOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {file.can_share && (
        <Tooltip title="Share with another department">
          <IconButton size="small" onClick={onShare}>
            <ShareOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {file.can_delete && (
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete}>
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  </Box>
);

/**
 * In-app viewer. Images, PDFs, text, audio and browser-playable video are
 * shown here; anything else gets a download button.
 *
 * The URL is a signed link good for ten minutes. A video left paused longer
 * than that will refuse to seek - reopening the preview issues a fresh link.
 */
export const PreviewDialog = ({ ui, preview, onClose, onDownload }) => {
  const file = preview?.file;
  const ext = String(file?.extension || "").toLowerCase();
  const asFrame = ["pdf", "txt", "csv", "md"].includes(ext);

  return (
    <Dialog open={!!preview} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
        <Typography component="span" fontWeight={800} noWrap sx={{ display: "block" }}>
          {file?.original_name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: ui.surfaceAlt, minHeight: 300 }}>
        {preview?.loading && (
          <Box sx={{ display: "grid", placeItems: "center", minHeight: 300 }}>
            <CircularProgress />
          </Box>
        )}
        {preview?.error && <Alert severity="warning">{preview.error}</Alert>}
        {preview?.url && file?.kind === "image" && (
          <Box sx={{ textAlign: "center" }}>
            <Box
              component="img"
              src={preview.url}
              alt={file.original_name}
              sx={{ maxWidth: "100%", maxHeight: "72vh", borderRadius: 1 }}
            />
          </Box>
        )}
        {preview?.url && asFrame && (
          <Box
            component="iframe"
            src={preview.url}
            title={file.original_name}
            sx={{ width: "100%", height: "72vh", border: 0, bgcolor: "#fff", borderRadius: 1 }}
          />
        )}
        {preview?.url && file?.kind === "audio" && (
          <Box sx={{ display: "grid", placeItems: "center", minHeight: 160 }}>
            <audio controls src={preview.url} style={{ width: "100%", maxWidth: 560 }} />
          </Box>
        )}
        {preview?.url && file?.kind === "video" && (
          <video
            controls
            src={preview.url}
            style={{ width: "100%", maxHeight: "72vh", background: "#000", borderRadius: 4 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onDownload} startIcon={<DownloadOutlinedIcon />}>
          Download
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ShareDialog = ({ file, departments, ownKey, selected, onToggle, onClose, onSave, saving }) => (
  <Dialog open={!!file} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 800 }}>Share with other departments</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" sx={{ mb: 1.5 }} noWrap title={file?.original_name}>
        <strong>{file?.original_name}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        The departments you tick can open and download this file. They cannot change,
        delete or pass it on.
      </Typography>
      <Stack>
        {departments
          .filter((k) => k !== ownKey)
          .map((k) => (
            <FormControlLabel
              key={k}
              control={<Checkbox checked={selected.includes(k)} onChange={() => onToggle(k)} />}
              label={k}
            />
          ))}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" onClick={onSave} disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </Button>
    </DialogActions>
  </Dialog>
);

/* ================================================================== */

const FolderPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);
  const navigate = useNavigate();
  const { folderId } = useParams();
  const isShared = folderId === "shared";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ departments: [], max_upload_bytes: 0, allowed_extensions: [] });
  const [search, setSearch] = useState("");

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failures, setFailures] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creating, setCreating] = useState(false);

  const [shareFile, setShareFile] = useState(null);
  const [shareKeys, setShareKeys] = useState([]);
  const [savingShare, setSavingShare] = useState(false);

  const [confirm, setConfirm] = useState(null); // { type: 'file'|'folder', item }
  const [preview, setPreview] = useState(null);

  /* ------------------------------------------------------- loading */

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await filesCall("folder", { id: folderId });
    setLoading(false);
    if (!res?.success) {
      setError(res?.message || "Could not open this folder");
      setData(null);
      return;
    }
    setData(res.data);
  }, [folderId]);

  useEffect(() => {
    load();
  }, [load]);

  // Department list and upload limits - the server's, not a copy kept here.
  useEffect(() => {
    let alive = true;
    filesCall("roots").then((res) => {
      if (alive && res?.success) {
        setMeta({
          departments: res.data.departments || [],
          max_upload_bytes: res.data.max_upload_bytes || 0,
          allowed_extensions: res.data.allowed_extensions || [],
        });
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  /* ------------------------------------------------------- derived */

  const files = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = data?.files || [];
    return q ? all.filter((f) => f.original_name.toLowerCase().includes(q)) : all;
  }, [data, search]);

  const groups = useMemo(
    () =>
      KIND_GROUPS.map((g) => ({ ...g, files: files.filter((f) => f.kind === g.kind) })).filter(
        (g) => g.files.length > 0
      ),
    [files]
  );

  /* ------------------------------------------------------- uploads */

  const upload = useCallback(
    async (list) => {
      const picked = Array.from(list || []);
      if (!picked.length || !data?.can_upload) return;

      // Fast, friendly refusal for the obvious cases. The server checks again.
      const max = meta.max_upload_bytes;
      const tooBig = max ? picked.filter((f) => f.size > max) : [];
      const ok = picked.filter((f) => !tooBig.includes(f));
      if (tooBig.length) {
        toast.error(
          `${tooBig.length} file(s) over the ${fmtBytes(max)} limit: ${tooBig
            .map((f) => f.name)
            .join(", ")}`
        );
      }
      if (!ok.length) return;

      setUploading(true);
      setProgress(0);
      setFailures([]);
      const res = await filesCall("upload", { folder_id: data.folder.id }, ok, setProgress);
      setUploading(false);

      if (!res?.success) {
        toast.error(res?.message || "Upload failed");
        return;
      }
      const failed = (res.data?.results || []).filter((r) => !r.ok);
      setFailures(failed);
      if (res.data?.uploaded) toast.success(res.message);
      if (failed.length) toast.warn(`${failed.length} file(s) were not accepted`);
      load();
    },
    [data, meta.max_upload_bytes, load]
  );

  /* ------------------------------------------------------- actions */

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setCreating(true);
    const res = await filesCall("create_folder", { parent_id: data.folder.id, name });
    setCreating(false);
    if (!res?.success) {
      toast.error(res?.message || "Could not create the folder");
      return;
    }
    toast.success(res.message || "Folder created");
    setNewFolderOpen(false);
    setNewFolderName("");
    load();
  };

  const openPreview = async (file) => {
    setPreview({ file, loading: true });
    const { url, message } = await fileLink(file.id, false);
    setPreview(
      url ? { file, url } : { file, error: message || "This file cannot be previewed" }
    );
  };

  const download = async (file) => {
    const msg = await downloadFile(file.id);
    if (msg) toast.error(msg);
  };

  const openShare = (file) => {
    setShareFile(file);
    setShareKeys(file.shares || []);
  };

  const saveShare = async () => {
    setSavingShare(true);
    const res = await filesCall("share", { id: shareFile.id, keys: shareKeys });
    setSavingShare(false);
    if (!res?.success) {
      toast.error(res?.message || "Could not update sharing");
      return;
    }
    toast.success(res.message);
    setShareFile(null);
    load();
  };

  const confirmDelete = async () => {
    if (!confirm) return;
    const { type, item } = confirm;
    const res = await filesCall(type === "file" ? "delete_file" : "delete_folder", { id: item.id });
    setConfirm(null);
    if (!res?.success) {
      toast.error(res?.message || "Could not delete");
      return;
    }
    toast.success(res.message);
    load();
  };

  /* ================================================================ */

  const folder = data?.folder;
  const accept = meta.allowed_extensions.map((e) => `.${e}`).join(",");

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", py: 3 }}>
      <ToastContainer position="top-right" autoClose={4000} theme={isDark ? "dark" : "light"} />
      <Container maxWidth="xl">
        {/* ---------------- Breadcrumb ---------------- */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate("/tasks/files")}
          >
            Files
          </Link>
          {isShared && <Typography color={ui.textPrimary}>Shared with my department</Typography>}
          {(data?.breadcrumb || []).map((c, i, arr) =>
            i === arr.length - 1 ? (
              <Typography key={c.id} color={ui.textPrimary} fontWeight={700}>
                {c.name}
              </Typography>
            ) : (
              <Link
                key={c.id}
                component="button"
                underline="hover"
                color="inherit"
                onClick={() => navigate(`/tasks/files/${c.id}`)}
              >
                {c.name}
              </Link>
            )
          )}
        </Breadcrumbs>

        {error && (
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: "12px" }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate("/tasks/files")}>
                Back to Files
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* ---------------- Header ---------------- */}
        {folder && (
          <Box
            sx={{
              bgcolor: ui.surface,
              border: `1px solid ${ui.border}`,
              borderRadius: "14px",
              p: { xs: 2, md: 2.5 },
              mb: 3,
              boxShadow: ui.shadow,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              gap={2}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Typography variant="h5" fontWeight={800} color={ui.textPrimary}>
                  {folder.name}
                </Typography>
                {folder.is_system && !folder.virtual && (
                  <Tooltip title="Default department folder - cannot be deleted">
                    <LockOutlinedIcon sx={{ fontSize: 18, color: ui.textSecondary }} />
                  </Tooltip>
                )}
                <Chip size="small" label={`${data.files.length} file(s)`} />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search this folder"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                {data.can_create_folder && (
                  <Button
                    variant="outlined"
                    startIcon={<CreateNewFolderOutlinedIcon />}
                    onClick={() => setNewFolderOpen(true)}
                  >
                    New folder
                  </Button>
                )}
                {data.can_upload && (
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadOutlinedIcon />}
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    sx={{
                      background: ui.gradient,
                      fontWeight: 700,
                      "&:hover": { background: ui.gradient, filter: "brightness(1.08)" },
                    }}
                  >
                    Upload
                  </Button>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  hidden
                  accept={accept}
                  onChange={(e) => {
                    upload(e.target.files);
                    e.target.value = "";
                  }}
                />
              </Stack>
            </Stack>
            {isShared && (
              <Typography variant="body2" color={ui.textSecondary} sx={{ mt: 1 }}>
                Files other departments have shared with yours. You can open and download
                them; only their own department can change them.
              </Typography>
            )}
          </Box>
        )}

        {loading && <Skeleton variant="rounded" height={220} sx={{ borderRadius: "14px" }} />}

        {!loading && data && (
          <>
            {/* ---------------- Drop zone ---------------- */}
            {data.can_upload && (
              <Box
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  upload(e.dataTransfer.files);
                }}
                sx={{
                  border: `2px dashed ${dragOver ? ui.accent : alpha(ui.border, 1)}`,
                  bgcolor: dragOver ? alpha(ui.accent, 0.06) : "transparent",
                  borderRadius: "14px",
                  p: 2,
                  mb: 3,
                  textAlign: "center",
                  transition: "all .15s ease",
                }}
              >
                {uploading ? (
                  <Box>
                    <Typography variant="body2" color={ui.textSecondary} sx={{ mb: 1 }}>
                      Uploading… {progress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
                  </Box>
                ) : (
                  <Typography variant="body2" color={ui.textSecondary}>
                    Drag files here to upload
                    {meta.max_upload_bytes ? ` · up to ${fmtBytes(meta.max_upload_bytes)} each` : ""}
                  </Typography>
                )}
              </Box>
            )}

            {failures.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: "12px" }} onClose={() => setFailures([])}>
                {failures.map((f) => (
                  <div key={f.name}>
                    <strong>{f.name}</strong> - {f.message}
                  </div>
                ))}
              </Alert>
            )}

            {/* ---------------- Subfolders ---------------- */}
            {data.subfolders.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={800} color={ui.textSecondary} sx={{ mb: 1.2 }}>
                  FOLDERS
                </Typography>
                <Grid container spacing={1.5}>
                  {data.subfolders.map((sf) => (
                    <Grid key={sf.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                      <SubfolderCard
                        ui={ui}
                        folder={sf}
                        onOpen={() => navigate(`/tasks/files/${sf.id}`)}
                        onDelete={() => setConfirm({ type: "folder", item: sf })}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* ---------------- Files, by kind ---------------- */}
            {groups.map((g) => (
              <Accordion
                key={g.kind}
                defaultExpanded
                disableGutters
                sx={{
                  bgcolor: ui.surface,
                  border: `1px solid ${ui.border}`,
                  borderRadius: "14px !important",
                  mb: 2,
                  boxShadow: ui.shadow,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box sx={{ color: kindColor(g.kind), display: "flex" }}>
                      {fileIcon({ kind: g.kind }, { fontSize: 22 })}
                    </Box>
                    <Typography fontWeight={800} color={ui.textPrimary}>
                      {g.label}
                    </Typography>
                    <Chip size="small" label={g.files.length} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {g.files.map((f) => (
                    <FileRow
                      key={f.id}
                      ui={ui}
                      file={f}
                      showOrigin={isShared}
                      onPreview={() => openPreview(f)}
                      onDownload={() => download(f)}
                      onShare={() => openShare(f)}
                      onDelete={() => setConfirm({ type: "file", item: f })}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}

            {groups.length === 0 && data.subfolders.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <FolderOutlinedIcon sx={{ fontSize: 48, color: alpha(ui.textSecondary, 0.5) }} />
                <Typography color={ui.textSecondary} sx={{ mt: 1 }}>
                  {search
                    ? "Nothing here matches your search."
                    : isShared
                    ? "Nothing has been shared with your department yet."
                    : "This folder is empty."}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>

      {/* ---------------- Dialogs ---------------- */}
      <Dialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>New folder in {folder?.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") createFolder();
            }}
            inputProps={{ maxLength: 120 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createFolder} disabled={creating || !newFolderName.trim()}>
            {creating ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Delete {confirm?.type === "file" ? "file" : "folder"}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{confirm?.item?.original_name || confirm?.item?.name}</strong>
            {confirm?.type === "file"
              ? " will be moved to the backup. Management can restore it, and the other members of this folder will be emailed."
              : " will be removed. Only empty folders can be deleted."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ShareDialog
        file={shareFile}
        departments={meta.departments}
        ownKey={shareFile?.access_key ?? folder?.access_key}
        selected={shareKeys}
        onToggle={(k) =>
          setShareKeys((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]))
        }
        onClose={() => setShareFile(null)}
        onSave={saveShare}
        saving={savingShare}
      />

      <PreviewDialog
        ui={ui}
        preview={preview}
        onClose={() => setPreview(null)}
        onDownload={() => preview?.file && download(preview.file)}
      />
    </Box>
  );
};

export default FolderPage;
